import React, { useEffect, useRef, useState } from 'react';

// ux
import LoadingAnimationSimple from '../ux/LoadingAnimationSimple';
import RadioButton from '../ux/RadioButton';

const HistoricalData = ({
  loadingTimeSeries,
  brandName,
  celebName,
  brandTimeSeries,
  celebrityTimeSeries,
  handleRetry
}) => {
  const [selectedSeries, setSelectedSeries] = useState('brand');
  const ref = useRef();
  const tooltipRef = useRef();

  useEffect(() => {
    // Ensure ref.current is defined
    if (!ref.current) return;

    // Ensure data is available
    if (!brandTimeSeries || !celebrityTimeSeries) return;

    if (!tooltipRef.current) {
      tooltipRef.current = d3
        .select('body')
        .append('div')
        .attr('class', 'tooltip')
        .style('position', 'absolute')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('text-align', 'center')
        .style('width', 'auto')
        .style('height', 'auto')
        .style('padding', '5px')
        .style('font', '12px sans-serif')
        .style('color', '#292b2c')
        .style('background', 'lightsteelblue')
        .style('border', '0px')
        .style('border-radius', '8px')
        .style('pointer-events', 'none')
        .style('opacity', 0);
    }

    const svg = d3
      .select(ref.current)
      .attr('width', '100%')
      .attr('height', 300);

    const width = ref.current.clientWidth;
    const margin = { top: 10, right: 30, bottom: 50, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = 300 - margin.top - margin.bottom;

    // Prepare data
    let data = [];
    if (selectedSeries === 'both') {
      data = [...brandTimeSeries, ...celebrityTimeSeries];
    } else if (selectedSeries === 'brand') {
      data = brandTimeSeries;
    } else if (selectedSeries === 'celebrity') {
      data = celebrityTimeSeries;
    }

    // Check if data is available
    if (!data || data.length === 0) return;

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(data, (d) => new Date(d.date)))
      .range([0, innerWidth]);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => +d.time_score)])
      .range([innerHeight, 0]);

    const line = d3
      .line()
      .x((d) => xScale(new Date(d.date)))
      .y((d) => yScale(+d.time_score));

    svg.selectAll('*').remove();

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const drawLine = (data, color) => {
      g.append('path')
        .datum(data)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 1.5)
        .attr('d', line);
    };

    if (selectedSeries === 'brand' || selectedSeries === 'both') {
      drawLine(brandTimeSeries, '#03ffff'); // Light blue
    }

    if (selectedSeries === 'celebrity' || selectedSeries === 'both') {
      drawLine(celebrityTimeSeries, '#0055fe'); // Darker blue
    }

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .style('color', '#fff')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)');

    g.append('g').call(d3.axisLeft(yScale)).style('color', '#fff');

    const focusLine = g
      .append('line')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .style('opacity', 0);

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .on('mouseover', () => {
        focusLine.style('opacity', 1);
        tooltipRef.current.style('opacity', 1);
      })
      .on('mouseout', () => {
        focusLine.style('opacity', 0);
        tooltipRef.current.style('opacity', 0);
      })
      .on('mousemove', function (event) {
        const x0 = xScale.invert(d3.pointer(event, this)[0]),
          bisectDate = d3.bisector((d) => new Date(d.date)).left;

        let data, label;

        if (selectedSeries === 'both') {
          const brandData = brandTimeSeries;
          const celebrityData = celebrityTimeSeries;

          let i = bisectDate(brandData, x0, 1),
            d0 = brandData[i - 1],
            d1 = brandData[i],
            dBrand = x0 - new Date(d0.date) > new Date(d1.date) - x0 ? d1 : d0;

          i = bisectDate(celebrityData, x0, 1);
          d0 = celebrityData[i - 1];
          d1 = celebrityData[i];
          let dCelebrity =
            x0 - new Date(d0.date) > new Date(d1.date) - x0 ? d1 : d0;

          focusLine.attr(
            'transform',
            `translate(${xScale(new Date(dBrand.date))},0)`
          );
          tooltipRef.current
            .html(
              `Date: ${dBrand.date}<br/>` +
                `${brandName} Score: ${dBrand.time_score}<br/>` +
                `${celebName} Score: ${dCelebrity.time_score}`
            )
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 28}px`);
        } else if (selectedSeries === 'brand') {
          data = brandTimeSeries;
          label = brandName;

          const i = bisectDate(data, x0, 1);
          const d0 = data[i - 1];
          const d1 = data[i];
          const d = x0 - new Date(d0.date) > new Date(d1.date) - x0 ? d1 : d0;

          focusLine.attr(
            'transform',
            `translate(${xScale(new Date(d.date))},0)`
          );
          tooltipRef.current
            .html(`Date: ${d.date}<br/>${label} Score: ${d.time_score}`)
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 28}px`);
        } else if (selectedSeries === 'celebrity') {
          data = celebrityTimeSeries;
          label = celebName;

          const i = bisectDate(data, x0, 1);
          const d0 = data[i - 1];
          const d1 = data[i];
          const d = x0 - new Date(d0.date) > new Date(d1.date) - x0 ? d1 : d0;

          focusLine.attr(
            'transform',
            `translate(${xScale(new Date(d.date))},0)`
          );
          tooltipRef.current
            .html(`Date: ${d.date}<br/>${label} Score: ${d.time_score}`)
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 28}px`);
        }
      });
  }, [loadingTimeSeries, brandTimeSeries, celebrityTimeSeries, selectedSeries]);

  return (
    <>
      {loadingTimeSeries ? (
        <div className="loading-animation-flex">
          <LoadingAnimationSimple />
        </div>
      ) : brandTimeSeries != null && celebrityTimeSeries != null ? (
        <div className="fuselight-historical-data">
          <div className="flex-container">
            <div className="series-selector">
              <RadioButton
                className={
                  selectedSeries === 'brand'
                    ? 'fuselight-radio active'
                    : 'fuselight-radio'
                }
                label={brandName}
                name="dataSeries"
                value="brand"
                checked={selectedSeries === 'brand'}
                onChange={() => setSelectedSeries('brand')}
              />
              <RadioButton
                className={
                  selectedSeries === 'celebrity'
                    ? 'fuselight-radio tertiary active'
                    : 'fuselight-radio tertiary'
                }
                label={celebName}
                name="dataSeries"
                value="celebrity"
                checked={selectedSeries === 'celebrity'}
                onChange={() => setSelectedSeries('celebrity')}
              />
              <RadioButton
                className={
                  selectedSeries === 'both'
                    ? 'fuselight-radio secondary active'
                    : 'fuselight-radio secondary'
                }
                label="Both"
                name="dataSeries"
                value="both"
                checked={selectedSeries === 'both'}
                onChange={() => setSelectedSeries('both')}
              />
            </div>
            <svg ref={ref}></svg>
          </div>
        </div>
      ) : (
        <div className="retry-flex">
          <p className="retry-header">Something went wrong...</p>
          <button className="btn btn--primary-outline" onClick={handleRetry}>
            Retry
          </button>
        </div>
      )}
    </>
  );
};

export default HistoricalData;
